// Day 1 - Full Implementation (compressed: old Day 1 + Day 2)
// Duration: 15+ minutes of continuous activities
// 6 exercises, 40 steps total for deep engagement

export const day1Content = {
  day_number: 1,
  title: "Twój pierwszy skuteczny prompt",
  subtitle: "Fundamenty + Struktura",
  duration: "15-18 min",
  compressed_from: "Dawny Dzień 1 + Dzień 2",
  exercises: [
    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 1: Czym jest prompt? (7 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "ex1",
      title: "Czym jest prompt?",
      icon: "lightbulb",
      steps: [
        {
          id: "ex1_s1",
          type: "choice",
          title: "Na początek",
          content: "Zanim zaczniemy – sprawdźmy, co już wiesz. Prompt to:",
          options: [
            { id: "a", text: "Polecenie dla AI napisane w języku naturalnym", correct: true },
            { id: "b", text: "Kod programistyczny w Pythonie", correct: false },
            { id: "c", text: "Specjalny język tylko dla ChatGPT", correct: false },
            { id: "d", text: "Formularz z polami do wypełnienia", correct: false }
          ],
          shuffle: true,
          softHint: "Prompt to po prostu tekst, który piszesz do AI. Nie musisz znać żadnego kodu.",
          successMessage: "Dokładnie! Prompt to zwykły tekst w Twoim języku – nic skomplikowanego."
        },
        {
          id: "ex1_s2",
          type: "choice",
          title: "Klucz do sukcesu",
          content: "Co najbardziej wpływa na jakość odpowiedzi AI?",
          options: [
            { id: "a", text: "Model AI (GPT-4, Claude itp.)", correct: false },
            { id: "b", text: "Jakość i precyzja promptu", correct: true },
            { id: "c", text: "Długość pytania", correct: false },
            { id: "d", text: "Pora dnia", correct: false }
          ],
          shuffle: true,
          softHint: "Nawet najlepszy model nie pomoże, jeśli nie wie, czego dokładnie potrzebujesz.",
          successMessage: "Świetnie! To TY masz kontrolę nad jakością odpowiedzi, nie AI."
        },
        {
          id: "ex1_s3",
          type: "choice",
          title: "Prawda czy fałsz?",
          content: "\"Dłuższy prompt = lepsza odpowiedź\" – czy to prawda?",
          options: [
            { id: "a", text: "Tak, im więcej napiszesz, tym lepiej", correct: false },
            { id: "b", text: "Nie, liczy się precyzja, nie długość", correct: true },
            { id: "c", text: "Tak, ale tylko po angielsku", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl: czy 100 słów bez sensu jest lepsze niż 20 słów z konkretami?",
          successMessage: "Dokładnie! 20 precyzyjnych słów > 100 słów bez sensu."
        },
        {
          id: "ex1_s4",
          type: "choice",
          title: "Rozpoznaj różnicę",
          content: "Który z poniższych jest DOBRYM promptem?",
          options: [
            { id: "a", text: "Napisz mi coś o marketingu", correct: false },
            { id: "b", text: "Pomóż mi z tekstem", correct: false },
            { id: "c", text: "Napisz 3 nagłówki reklamowe dla sklepu z kawą, grupą docelową są młodzi profesjonaliści", correct: true },
            { id: "d", text: "Zrób coś kreatywnego", correct: false }
          ],
          shuffle: true,
          softHint: "Dobry prompt zawiera konkret: co, dla kogo, w jakim stylu.",
          successMessage: "Świetnie! Ten prompt ma konkretne zadanie, grupę docelową i kontekst."
        },
        {
          id: "ex1_s5",
          type: "reorder",
          title: "Ułóż proces",
          content: "Ułóż etapy pracy z AI w logicznej kolejności:",
          items: [
            { id: "r1", text: "Określ cel (co chcesz osiągnąć)", order: 1 },
            { id: "r2", text: "Napisz prompt", order: 2 },
            { id: "r3", text: "Przeczytaj odpowiedź AI", order: 3 },
            { id: "r4", text: "Doprecyzuj lub zaakceptuj", order: 4 }
          ],
          softHint: "Najpierw musisz wiedzieć, czego chcesz. Potem dopiero piszesz.",
          successMessage: "Dokładnie taki jest flow pracy z AI!"
        },
        {
          id: "ex1_s6",
          type: "choice",
          title: "Kluczowa zasada",
          content: "Im bardziej _______ prompt, tym lepsza odpowiedź AI. Co pasuje najlepiej?",
          options: [
            { id: "a", text: "Precyzyjny", correct: true },
            { id: "b", text: "Długi", correct: false },
            { id: "c", text: "Skomplikowany", correct: false },
            { id: "d", text: "Formalny", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl: co sprawia, że AI wie dokładnie, czego potrzebujesz?",
          successMessage: "Właśnie! Precyzja to Twój najlepszy przyjaciel."
        },
        {
          id: "ex1_s7",
          type: "choice",
          title: "Podsumowanie ćwiczenia",
          content: "Co jest najważniejszym wnioskiem z tego ćwiczenia?",
          options: [
            { id: "a", text: "Prompt to zwykły tekst, a jego jakość decyduje o odpowiedzi", correct: true },
            { id: "b", text: "Trzeba pisać bardzo długie prompty", correct: false },
            { id: "c", text: "AI zawsze da dobrą odpowiedź", correct: false }
          ],
          shuffle: false,
          softHint: "Przypomnij sobie główną myśl ćwiczenia.",
          successMessage: "Perfekcyjnie! Przejdźmy do struktury promptu."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 2: Magiczna formuła RZKF (8 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "ex2",
      title: "Magiczna formuła RZKF",
      icon: "layers",
      steps: [
        {
          id: "ex2_s1",
          type: "choice",
          title: "Wprowadzenie do formuły",
          content: "Skuteczny prompt składa się z 4 elementów. Która kombinacja jest poprawna?",
          options: [
            { id: "a", text: "ROLA + ZADANIE + KONTEKST + FORMAT", correct: true },
            { id: "b", text: "Tylko pytanie", correct: false },
            { id: "c", text: "Jak najdłuższy tekst", correct: false },
            { id: "d", text: "Emoji i pozdrowienia", correct: false }
          ],
          shuffle: true,
          softHint: "Skuteczny prompt ma strukturę: kto, co, w jakim kontekście, jak.",
          successMessage: "To jest formuła RZKF – zapamiętaj ją dobrze!"
        },
        {
          id: "ex2_s2",
          type: "choice",
          title: "R jak ROLA",
          content: "ROLA mówi AI:",
          options: [
            { id: "a", text: "Kim ma być (ekspert, asystent, copywriter...)", correct: true },
            { id: "b", text: "Ile słów ma napisać", correct: false },
            { id: "c", text: "Dla kogo tworzy treść", correct: false },
            { id: "d", text: "Jaki format ma odpowiedź", correct: false }
          ],
          shuffle: true,
          softHint: "ROLA = tożsamość AI. \"Jesteś ekspertem od...\"",
          successMessage: "Tak! ROLA nadaje AI specjalizację i perspektywę."
        },
        {
          id: "ex2_s3",
          type: "choice",
          title: "T jak ZADANIE",
          content: "ZADANIE określa:",
          options: [
            { id: "a", text: "CO AI ma zrobić (napisz, stwórz, zaplanuj...)", correct: true },
            { id: "b", text: "KIM jest AI", correct: false },
            { id: "c", text: "DLA KOGO jest treść", correct: false },
            { id: "d", text: "ILE słów ma być", correct: false }
          ],
          shuffle: true,
          softHint: "ZADANIE to konkretna akcja: napisz, stwórz, przeanalizuj.",
          successMessage: "Dokładnie! ZADANIE to serce promptu."
        },
        {
          id: "ex2_s4",
          type: "choice",
          title: "K jak KONTEKST",
          content: "KONTEKST odpowiada na pytanie:",
          options: [
            { id: "a", text: "Dla kogo? W jakiej sytuacji? Jaki problem?", correct: true },
            { id: "b", text: "Kim jest AI?", correct: false },
            { id: "c", text: "Ile słów napisać?", correct: false },
            { id: "d", text: "Co zrobić?", correct: false }
          ],
          shuffle: true,
          softHint: "KONTEKST to tło: dla kogo, w jakiej sytuacji, jaki cel.",
          successMessage: "Świetnie! KONTEKST personalizuje odpowiedź."
        },
        {
          id: "ex2_s5",
          type: "choice",
          title: "F jak FORMAT",
          content: "FORMAT określa:",
          options: [
            { id: "a", text: "JAK ma wyglądać odpowiedź (lista, tabela, długość...)", correct: true },
            { id: "b", text: "KIM jest AI", correct: false },
            { id: "c", text: "CO AI ma zrobić", correct: false },
            { id: "d", text: "DLA KOGO jest treść", correct: false }
          ],
          shuffle: true,
          softHint: "FORMAT to struktura: lista, tabela, 100 słów, 5 punktów.",
          successMessage: "Dokładnie! FORMAT kontroluje wygląd odpowiedzi."
        },
        {
          id: "ex2_s6",
          type: "reorder",
          title: "Ułóż formułę",
          content: "Ułóż elementy formuły w zalecanej kolejności:",
          items: [
            { id: "r1", text: "ROLA (kim jest AI)", order: 1 },
            { id: "r2", text: "ZADANIE (co zrobić)", order: 2 },
            { id: "r3", text: "KONTEKST (dla kogo, dlaczego)", order: 3 },
            { id: "r4", text: "FORMAT (jak ma wyglądać)", order: 4 }
          ],
          softHint: "Kolejność: R → Z → K → F (RZKF).",
          successMessage: "Tak właśnie buduje się profesjonalny prompt!"
        },
        {
          id: "ex2_s7",
          type: "classify",
          title: "Rozpoznaj elementy",
          content: "Przypisz fragmenty do odpowiednich części formuły:",
          categories: ["ROLA", "ZADANIE", "KONTEKST", "FORMAT"],
          items: [
            { id: "c1", text: "Jesteś ekspertem od HR", category: "ROLA" },
            { id: "c2", text: "Napisz ogłoszenie o pracę", category: "ZADANIE" },
            { id: "c3", text: "dla startupu technologicznego", category: "KONTEKST" },
            { id: "c4", text: "w 150 słowach, z emoji", category: "FORMAT" }
          ],
          softHint: "ROLA = kto, ZADANIE = co, KONTEKST = dla kogo, FORMAT = jak.",
          successMessage: "Perfekcyjnie! Rozumiesz strukturę promptu."
        },
        {
          id: "ex2_s8",
          type: "fill",
          title: "Zapamiętaj skrót",
          content: "Wpisz skrót formuły skutecznego promptu:",
          template: "Formuła skutecznego promptu to: ______",
          correctAnswers: ["RZKF", "rzkf", "R Z K F", "R-Z-K-F"],
          minLength: 3,
          softHint: "4 litery od: Rola, Zadanie, Kontekst, Format.",
          successMessage: "RZKF – Twoja nowa supermoc!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 3: Praktyka formuły RZKF (7 kroków) - UPROSZCZONE
    // ═══════════════════════════════════════════════════════════════
    {
      id: "ex3",
      title: "Praktyka formuły RZKF",
      icon: "edit",
      steps: [
        {
          id: "ex3_s1",
          type: "choice",
          title: "Kluczowe pytanie",
          content: "Co najbardziej wpływa na jakość odpowiedzi AI?",
          options: [
            { id: "a", text: "Pora dnia", correct: false },
            { id: "b", text: "Długość pytania", correct: false },
            { id: "c", text: "Jakość i precyzja promptu", correct: true },
            { id: "d", text: "Model AI", correct: false }
          ],
          shuffle: true,
          softHint: "Pomyśl: co TY kontrolujesz w rozmowie z AI?",
          successMessage: "Dokładnie! Twój prompt decyduje o jakości odpowiedzi."
        },
        {
          id: "ex3_s2",
          type: "choice",
          title: "Porównaj prompty",
          content: "Który prompt jest lepszy?",
          options: [
            { id: "a", text: "Napisz coś o marketingu", correct: false },
            { id: "b", text: "Pomóż mi", correct: false },
            { id: "c", text: "Jako copywriter, napisz 3 nagłówki reklamowe dla kawy, grupa: młodzi profesjonaliści", correct: true },
            { id: "d", text: "Marketing 2025", correct: false }
          ],
          shuffle: true,
          softHint: "Dobry prompt ma konkret: kto, co, dla kogo, jak.",
          successMessage: "Świetnie! Ten prompt ma ROLĘ, ZADANIE, KONTEKST."
        },
        {
          id: "ex3_s3",
          type: "choice",
          title: "Element ROLA",
          content: "Który fragment to ROLA?",
          options: [
            { id: "a", text: "Jesteś ekspertem od fitness", correct: true },
            { id: "b", text: "Napisz plan treningowy", correct: false },
            { id: "c", text: "dla początkującego", correct: false },
            { id: "d", text: "w formie listy", correct: false }
          ],
          shuffle: true,
          softHint: "ROLA mówi AI, kim ma być.",
          successMessage: "Tak! ROLA zawsze zaczyna się od 'Jesteś...'."
        },
        {
          id: "ex3_s4",
          type: "choice",
          title: "Element ZADANIE",
          content: "Który fragment to ZADANIE?",
          options: [
            { id: "a", text: "Jesteś ekspertem", correct: false },
            { id: "b", text: "Napisz plan treningowy", correct: true },
            { id: "c", text: "dla kobiety 30 lat", correct: false },
            { id: "d", text: "max 200 słów", correct: false }
          ],
          shuffle: true,
          softHint: "ZADANIE to akcja: napisz, stwórz, zaplanuj.",
          successMessage: "Dokładnie! ZADANIE mówi, CO AI ma zrobić."
        },
        {
          id: "ex3_s5",
          type: "choice",
          title: "Element FORMAT",
          content: "Który fragment to FORMAT?",
          options: [
            { id: "a", text: "Jesteś trenerem", correct: false },
            { id: "b", text: "Stwórz dietę", correct: false },
            { id: "c", text: "dla sportowca", correct: false },
            { id: "d", text: "w formie tabeli na 7 dni", correct: true }
          ],
          shuffle: true,
          softHint: "FORMAT określa wygląd odpowiedzi.",
          successMessage: "Tak! FORMAT to struktura: lista, tabela, długość."
        },
        {
          id: "ex3_s6",
          type: "choice",
          title: "Sprawdź wiedzę",
          content: "Który element promptu mówi AI JAK ma wyglądać odpowiedź?",
          options: [
            { id: "a", text: "ROLA", correct: false },
            { id: "b", text: "ZADANIE", correct: false },
            { id: "c", text: "KONTEKST", correct: false },
            { id: "d", text: "FORMAT", correct: true }
          ],
          shuffle: true,
          softHint: "Który element kontroluje strukturę i długość?",
          successMessage: "Tak! FORMAT kontroluje wygląd – zapamiętaj to."
        },
        {
          id: "ex3_s7",
          type: "choice",
          title: "Podsumowanie",
          content: "Ćwiczenie ukończone! Który element dodajesz PIERWSZY?",
          options: [
            { id: "a", text: "ROLA – najpierw określam, kim jest AI", correct: true },
            { id: "b", text: "FORMAT – najpierw mówię, jak ma wyglądać", correct: false },
            { id: "c", text: "Nie ma znaczenia", correct: false }
          ],
          shuffle: false,
          softHint: "RZKF – pierwsza litera to R jak ROLA.",
          successMessage: "Świetnie! Masz już solidne podstawy."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 4: Diagnoza i naprawa promptów (7 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "ex4",
      title: "Diagnoza słabych promptów",
      icon: "search",
      steps: [
        {
          id: "ex4_s1",
          type: "choice",
          title: "Znajdź problem #1",
          content: "Co jest nie tak z tym promptem?\n\n\"Napisz mi coś o sprzedaży\"",
          options: [
            { id: "a", text: "Jest za krótki", correct: false },
            { id: "b", text: "Brak konkretu – AI nie wie CO, DLA KOGO, W JAKIEJ FORMIE", correct: true },
            { id: "c", text: "Brakuje emoji", correct: false },
            { id: "d", text: "Powinien być po angielsku", correct: false }
          ],
          shuffle: true,
          softHint: "Czy AI wie, czego dokładnie potrzebujesz po przeczytaniu tego promptu?",
          successMessage: "Dokładnie! Brak precyzji to główny problem."
        },
        {
          id: "ex4_s2",
          type: "choice",
          title: "Napraw prompt",
          content: "Która wersja promptu jest najlepsza?",
          options: [
            { id: "a", text: "Napisz mi coś o sprzedaży", correct: false },
            { id: "b", text: "Pomóż mi w sprzedaży", correct: false },
            { id: "c", text: "Jako ekspert sprzedaży, napisz ogłoszenie dla pracowników salonu auto w Polsce, w formie listy", correct: true },
            { id: "d", text: "Sprzedaż 2025 – coś napisz", correct: false }
          ],
          shuffle: true,
          softHint: "Dobry prompt ma ROLĘ, ZADANIE, KONTEKST i FORMAT.",
          successMessage: "Świetnie! Ten prompt ma wszystkie elementy RZKF."
        },
        {
          id: "ex4_s3",
          type: "choice",
          title: "Znajdź problem #2",
          content: "Co jest nie tak z tym promptem?\n\n\"Pomóż mi z prezentacją\"",
          options: [
            { id: "a", text: "Brak ROLI dla AI", correct: false },
            { id: "b", text: "Brak informacji: jaka prezentacja, na jaki temat, dla kogo", correct: true },
            { id: "c", text: "Jest zbyt grzeczny", correct: false },
            { id: "d", text: "Wszystko jest OK", correct: false }
          ],
          shuffle: true,
          softHint: "AI nie wie nic o prezentacji. Pomoc w czym konkretnie?",
          successMessage: "Tak! Zero kontekstu to zero dobrej pomocy."
        },
        {
          id: "ex4_s4",
          type: "fixPrompt",
          title: "Napraw prompt #2",
          content: "Popraw ten prompt używając formuły RZKF:",
          originalPrompt: "Pomóż mi z prezentacją",
          minLength: 60,
          requiredElements: ["prezentacj", "temat|o |slajd", "slajd|punkt|struktur|dla"],
          softHint: "Dodaj: jaka prezentacja? Na jaki temat? Ile slajdów? Dla jakiej publiczności?",
          exampleFix: "Jesteś konsultantem prezentacji. Stwórz strukturę 10-slajdowej prezentacji o AI w HR dla zarządu firmy. Dla każdego slajdu podaj tytuł i 3 punkty.",
          successMessage: "Profesjonalny prompt! Ogromna różnica, prawda?"
        },
        {
          id: "ex4_s5",
          type: "classify",
          title: "Kategorie błędów",
          content: "Przypisz problemy do kategorii błędu:",
          categories: ["Brak ROLI", "Brak ZADANIA", "Brak KONTEKSTU", "Brak FORMATU"],
          items: [
            { id: "c1", text: "AI nie wie, kim ma być (ekspert, asystent?)", category: "Brak ROLI" },
            { id: "c2", text: "AI nie wie, CO ma zrobić", category: "Brak ZADANIA" },
            { id: "c3", text: "AI nie wie, DLA KOGO tworzy treść", category: "Brak KONTEKSTU" },
            { id: "c4", text: "AI nie wie, JAK ma wyglądać odpowiedź", category: "Brak FORMATU" }
          ],
          softHint: "Każdy brak to inna litera z RZKF.",
          successMessage: "Rozpoznajesz już typy błędów. To klucz do naprawiania!"
        },
        {
          id: "ex4_s6",
          type: "fixPrompt",
          title: "Napraw prompt #3",
          content: "Ostatnia naprawa – daj z siebie wszystko:",
          originalPrompt: "Napisz email",
          minLength: 70,
          requiredElements: ["email", "do |dla |klient|szef", "ton|styl|profesjonaln|oficjaln|słów"],
          softHint: "Email do kogo? W jakiej sprawie? Jaki ton? Jaka długość?",
          exampleFix: "Jesteś asystentem biznesowym. Napisz profesjonalny email do klienta z przeprosinami za opóźnienie dostawy. Ton: przepraszający ale pewny. Max 100 słów. Zaproponuj 10% rabatu.",
          successMessage: "Mistrzowska naprawa! To jest profesjonalny prompt."
        },
        {
          id: "ex4_s7",
          type: "choice",
          title: "Złota zasada",
          content: "Co robisz, gdy AI daje słabą odpowiedź?",
          options: [
            { id: "a", text: "Zmieniam AI na inny model", correct: false },
            { id: "b", text: "Analizuję swój prompt i go poprawiam", correct: true },
            { id: "c", text: "Powtarzam to samo pytanie", correct: false },
            { id: "d", text: "Rezygnuję z AI", correct: false }
          ],
          shuffle: true,
          softHint: "Słaba odpowiedź = zwykle słaby prompt. Co z tym robisz?",
          successMessage: "Dokładnie! Iteracja promptu to klucz do sukcesu."
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 5: Twój własny prompt – misja końcowa (6 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "ex5",
      title: "Misja końcowa: Twój prompt",
      icon: "target",
      steps: [
        {
          id: "ex5_s1",
          type: "choice",
          title: "Wybierz branżę",
          content: "Wybierz branżę, dla której stworzysz własny profesjonalny prompt:",
          options: [
            { id: "a", text: "E-commerce (sklep online)", correct: true, isChoice: true },
            { id: "b", text: "Edukacja (kursy, szkolenia)", correct: true, isChoice: true },
            { id: "c", text: "Usługi lokalne (salon, restauracja)", correct: true, isChoice: true },
            { id: "d", text: "Marketing (social media, reklamy)", correct: true, isChoice: true }
          ],
          shuffle: false,
          softHint: "Wybierz to, co Cię najbardziej interesuje!",
          successMessage: "Świetny wybór! Teraz zbudujesz prawdziwy prompt."
        },
        {
          id: "ex5_s2",
          type: "shortPrompt",
          title: "ROLA dla Twojej branży",
          content: "Napisz ROLĘ dla AI w wybranej branży:",
          placeholder: "Jesteś...",
          minLength: 15,
          requiredElements: ["jesteś"],
          softHint: "Kim ma być AI? Ekspertem od czego?",
          successMessage: "Świetna rola!"
        },
        {
          id: "ex5_s3",
          type: "shortPrompt",
          title: "ZADANIE konkretne",
          content: "Co AI ma zrobić dla Twojej branży?",
          placeholder: "Napisz / Stwórz / Zaplanuj...",
          minLength: 20,
          requiredElements: ["napisz|stwórz|zaplanuj|przygotuj|wymyśl|opracuj"],
          softHint: "Konkretne działanie: napisz, stwórz, zaplanuj...",
          successMessage: "Jasne zadanie!"
        },
        {
          id: "ex5_s4",
          type: "shortPrompt",
          title: "KONTEKST szczegółowy",
          content: "Dla kogo? W jakiej sytuacji? Jaki problem rozwiązujesz?",
          placeholder: "dla... / w kontekście... / problem:",
          minLength: 25,
          requiredElements: ["dla|klient|użytkownik|osob|grup|firm|sklep|kurs"],
          softHint: "Określ odbiorcę i sytuację szczegółowo.",
          successMessage: "Świetny kontekst!"
        },
        {
          id: "ex5_s5",
          type: "shortPrompt",
          title: "FORMAT precyzyjny",
          content: "Jak ma wyglądać odpowiedź AI?",
          placeholder: "w formie... / max... słów / zawierający...",
          minLength: 15,
          requiredElements: ["forma|punkt|lista|słów|zdań|krótk|tabel|struktur"],
          softHint: "Ile punktów? Ile słów? Jaka struktura?",
          successMessage: "Precyzyjny format!"
        },
        {
          id: "ex5_s6",
          type: "buildPrompt",
          title: "Złóż finalny prompt",
          content: "Połącz wszystko w jeden profesjonalny prompt RZKF:",
          sections: ["ROLA", "ZADANIE", "KONTEKST", "FORMAT"],
          minTotalLength: 100,
          softHint: "To Twój pierwszy samodzielny profesjonalny prompt!",
          successMessage: "🎉 BRAWO! Stworzyłeś profesjonalny prompt!"
        }
      ]
    },

    // ═══════════════════════════════════════════════════════════════
    // ĆWICZENIE 6: Quiz końcowy + Podsumowanie (5 kroków)
    // ═══════════════════════════════════════════════════════════════
    {
      id: "ex6",
      title: "Quiz końcowy",
      icon: "trophy",
      steps: [
        {
          id: "ex6_s1",
          type: "choice",
          title: "Pytanie 1/4",
          content: "Jaki jest skrót formuły skutecznego promptu?",
          options: [
            { id: "a", text: "RZKF (Rola, Zadanie, Kontekst, Format)", correct: true },
            { id: "b", text: "ABCD (Analiza, Budowa, Cel, Dane)", correct: false },
            { id: "c", text: "QWER (Pytanie, Wizja, Efekt, Rezultat)", correct: false },
            { id: "d", text: "ZXCV (Zadanie, Xcel, Cel, Vibe)", correct: false }
          ],
          shuffle: true,
          softHint: "Przypomnij sobie 4 elementy: Rola, Zadanie, Kontekst, Format.",
          successMessage: "Świetnie! RZKF to Twoja formuła sukcesu."
        },
        {
          id: "ex6_s2",
          type: "choice",
          title: "Pytanie 2/4",
          content: "Co robisz NAJPIERW przy pisaniu promptu?",
          options: [
            { id: "a", text: "Określam ROLĘ – kim ma być AI", correct: true },
            { id: "b", text: "Piszę FORMAT – jak ma wyglądać odpowiedź", correct: false },
            { id: "c", text: "Dodaję emoji", correct: false },
            { id: "d", text: "Kopiuję prompt z internetu", correct: false }
          ],
          shuffle: true,
          softHint: "RZKF – która litera jest pierwsza?",
          successMessage: "Tak! Zawsze zaczynaj od ROLI."
        },
        {
          id: "ex6_s3",
          type: "choice",
          title: "Pytanie 3/4",
          content: "Gdy AI daje słabą odpowiedź, co jest NAJCZĘŚCIEJ przyczyną?",
          options: [
            { id: "a", text: "Słaby model AI", correct: false },
            { id: "b", text: "Zbyt mało precyzyjny prompt", correct: true },
            { id: "c", text: "Zła pora dnia", correct: false },
            { id: "d", text: "AI jest w złym humorze", correct: false }
          ],
          shuffle: true,
          softHint: "Pamiętaj: jakość odpowiedzi = jakość promptu.",
          successMessage: "Dokładnie! Zawsze najpierw sprawdź swój prompt."
        },
        {
          id: "ex6_s4",
          type: "choice",
          title: "Pytanie 4/4",
          content: "Element FORMAT określa:",
          options: [
            { id: "a", text: "JAK ma wyglądać odpowiedź (lista, długość, struktura)", correct: true },
            { id: "b", text: "KIM jest AI", correct: false },
            { id: "c", text: "CO AI ma zrobić", correct: false },
            { id: "d", text: "DLA KOGO jest odpowiedź", correct: false }
          ],
          shuffle: true,
          softHint: "FORMAT to ostatnia litera – odpowiada za WYGLĄD.",
          successMessage: "Perfekcyjnie! Zdałeś quiz!"
        },
        {
          id: "ex6_s5",
          type: "choice",
          title: "🎉 Gratulacje!",
          content: "Ukończyłeś Dzień 1! Co chcesz teraz zrobić?",
          options: [
            { id: "a", text: "Zobacz podsumowanie i zakończ", correct: true, isChoice: true },
            { id: "b", text: "Powtórz ćwiczenia od początku", correct: true, isChoice: true }
          ],
          shuffle: false,
          softHint: "Wybierz opcję, która Ci odpowiada.",
          successMessage: "🏆 Dzień 1 ukończony! Jutro Dzień 2 – zaawansowane techniki!"
        }
      ]
    }
  ]
};

// ═══════════════════════════════════════════════════════════════
// DNI 2, 3, 4+ – PLACEHOLDERS (nazwy + cele + opis kompresji)
// ═══════════════════════════════════════════════════════════════
export const day2Placeholder = {
  day_number: 2,
  title: "Zaawansowane techniki promptów",
  subtitle: "Chain-of-thought + Few-shot learning",
  duration: "10-15 min",
  compressed_from: "Dawny Dzień 3 + Dzień 4",
  goals: [
    "Nauczyć techniki chain-of-thought (krok po kroku)",
    "Wprowadzić few-shot learning (przykłady w prompcie)",
    "Ćwiczenia praktyczne z obiema technikami"
  ],
  exercises_planned: 4,
  status: "placeholder"
};

export const day3Placeholder = {
  day_number: 3,
  title: "Prompty dla konkretnych zastosowań",
  subtitle: "Copywriting + Analiza + Kod",
  duration: "10-15 min",
  compressed_from: "Dawny Dzień 5 + Dzień 6",
  goals: [
    "Prompty copywriterskie (nagłówki, opisy, CTA)",
    "Prompty analityczne (dane, raporty)",
    "Podstawy promptów do kodu"
  ],
  exercises_planned: 4,
  status: "placeholder"
};

export const day4PlusPlaceholder = {
  day_number: 4,
  title: "Zaawansowane zastosowania (PRO)",
  subtitle: "Dostęp po subskrypcji",
  duration: "10-15 min",
  compressed_from: "Dawne Dni 7+",
  goals: [
    "Automatyzacje z AI",
    "Własne szablony promptów",
    "Integracje i workflows"
  ],
  exercises_planned: 4,
  status: "paywall",
  price: "35 zł/miesiąc"
};

export const allDaysOverview = [
  day1Content,
  day2Placeholder,
  day3Placeholder,
  day4PlusPlaceholder
];
